import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Card,
    Row,
    Col,
    Avatar,
    Typography,
    Descriptions,
    Button,
    Space,
    Tag,
    Table,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Empty,
} from 'antd';
import {
    UserOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    AimOutlined,
    ClockCircleOutlined,
    MailOutlined,
    BugOutlined,
} from '@ant-design/icons';
import { usersApi, strategyApi, feedbackApi } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Title, Text, Paragraph } = Typography;

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role?: { name: string };
    lastLoginAt?: string;
    createdAt?: string;
}

interface Initiative {
    id: string;
    name: string;
    status: string;
    priority: string;
    area?: string;
}

const INITIATIVE_STATUSES: Record<string, { label: string; color: string }> = {
    PLANNED: { label: 'Planowana', color: 'default' },
    ACTIVE: { label: 'Aktywna', color: 'processing' },
    ON_HOLD: { label: 'Wstrzymana', color: 'warning' },
    CLOSED: { label: 'Zamknięta', color: 'success' },
    ABANDONED: { label: 'Porzucona', color: 'error' },
};

// Function to remove Polish characters for login generation
function removePolishChars(str: string): string {
    const map: Record<string, string> = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
        'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N',
        'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z',
    };
    return str.split('').map(char => map[char] || char).join('');
}

function generateLogin(firstName: string, lastName: string): string {
    const first = removePolishChars(firstName.toLowerCase().trim());
    const last = removePolishChars(lastName.toLowerCase().trim());
    return `${first}.${last}`;
}

export default function UserProfilePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuthStore();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [form] = Form.useForm();

    // Fetch user data
    const { data: user, isLoading: userLoading } = useQuery<User>({
        queryKey: ['user', id],
        queryFn: () => usersApi.getOne(id!).then((r: any) => r.data),
        enabled: !!id,
    });

    // Fetch all initiatives and filter where user is owner
    const { data: allInitiatives = [], isLoading: initiativesLoading } = useQuery<any[]>({
        queryKey: ['all-initiatives'],
        queryFn: () => strategyApi.getInitiatives().then((r: any) => r.data || []),
    });

    // Filter to only show initiatives where this user is the owner
    const initiatives = allInitiatives.filter((init: any) => init.owner?.id === id);

    const isOwnProfile = currentUser?.id === id;

    // Fetch user feedback
    const { data: myFeedback = [], isLoading: feedbackLoading } = useQuery<any[]>({
        queryKey: ['my-feedback', id],
        queryFn: () => feedbackApi.getMyFeedback().then((r: any) => r.data || []),
        enabled: isOwnProfile,
    });

    // Update user mutation
    const updateUserMutation = useMutation({
        mutationFn: (data: any) => usersApi.update(id!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user', id] });
            message.success('Profil zaktualizowany');
            setEditModalOpen(false);
        },
        onError: () => {
            message.error('Nie udało się zaktualizować profilu');
        },
    });

    const handleEdit = () => {
        form.setFieldsValue({
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
        });
        setEditModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            updateUserMutation.mutate(values);
        } catch (e) {
            // Validation error
        }
    };

    if (userLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!user) {
        return <Empty description="Użytkownik nie znaleziony" />;
    }

    const login = generateLogin(user.firstName, user.lastName);

    const initiativeColumns = [
        {
            title: 'Inicjatywa',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: Initiative) => (
                <a onClick={() => navigate('/strategy')}>{name}</a>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string) => {
                const statusInfo = INITIATIVE_STATUSES[status] || { label: status, color: 'default' };
                return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
            },
        },
        {
            title: 'Priorytet',
            dataIndex: 'priority',
            key: 'priority',
            width: 80,
            render: (priority: string) => {
                const color = priority === 'P0' ? 'red' : priority === 'P1' ? 'orange' : 'blue';
                return <Tag color={color}>{priority}</Tag>;
            },
        },
    ];

    const feedbackColumns = [
        {
            title: 'Tytuł',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string) => {
                const colors: Record<string, string> = {
                    OPEN: 'blue',
                    IN_PROGRESS: 'orange',
                    RESOLVED: 'green',
                    REJECTED: 'error',
                    CLOSED: 'default',
                };
                return <Tag color={colors[status] || 'default'}>{status}</Tag>;
            },
        },
        {
            title: 'Data',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
        },
    ];

    return (
        <div>
            <Row gutter={[24, 24]}>
                {/* Profile Card */}
                <Col xs={24} lg={8}>
                    <Card>
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <Avatar
                                size={120}
                                style={{
                                    backgroundColor: '#1890ff',
                                    marginBottom: 16,
                                    fontSize: 48,
                                }}
                            >
                                {user.firstName[0]}{user.lastName[0]}
                            </Avatar>
                            <Title level={3} style={{ marginBottom: 4 }}>
                                {user.firstName} {user.lastName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                @{login}
                            </Text>
                            {user.role && (
                                <div style={{ marginTop: 8 }}>
                                    <Tag color="blue">{user.role.name}</Tag>
                                </div>
                            )}
                        </div>

                        <Descriptions column={1} size="small">
                            <Descriptions.Item label={<><MailOutlined /> Email</>}>
                                {user.email}
                            </Descriptions.Item>
                            {user.lastLoginAt && (
                                <Descriptions.Item label={<><ClockCircleOutlined /> Ostatnie logowanie</>}>
                                    <span title={dayjs(user.lastLoginAt).format('DD.MM.YYYY HH:mm')}>
                                        {dayjs(user.lastLoginAt).fromNow()}
                                    </span>
                                </Descriptions.Item>
                            )}
                            {user.createdAt && (
                                <Descriptions.Item label="Konto utworzone">
                                    {dayjs(user.createdAt).format('DD.MM.YYYY')}
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {isOwnProfile && (
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                block
                                style={{ marginTop: 16 }}
                                onClick={handleEdit}
                            >
                                Edytuj profil
                            </Button>
                        )}
                    </Card>
                </Col>

                {/* Content Card */}
                <Col xs={24} lg={16}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card
                            title={
                                <Space>
                                    <AimOutlined />
                                    Przypisane inicjatywy strategiczne
                                </Space>
                            }
                        >
                            {initiatives.length === 0 ? (
                                <Empty
                                    description={`${isOwnProfile ? 'Nie masz' : 'Ten użytkownik nie ma'} przypisanych inicjatyw`}
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            ) : (
                                <Table
                                    dataSource={initiatives}
                                    columns={initiativeColumns}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    loading={initiativesLoading}
                                />
                            )}
                        </Card>

                        {isOwnProfile && (
                            <Card
                                title={
                                    <Space>
                                        <BugOutlined />
                                        Moje zgłoszenia błędów
                                    </Space>
                                }
                            >
                                <Table
                                    dataSource={myFeedback}
                                    columns={feedbackColumns}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    loading={feedbackLoading}
                                />
                            </Card>
                        )}
                    </Space>
                </Col>
            </Row>

            {/* Edit Profile Modal */}
            <Modal
                title="Edytuj profil"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setEditModalOpen(false)}>
                        Anuluj
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSave}
                        loading={updateUserMutation.isPending}
                    >
                        Zapisz
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="firstName"
                        label="Imię"
                        rules={[{ required: true, message: 'Imię jest wymagane' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label="Nazwisko"
                        rules={[{ required: true, message: 'Nazwisko jest wymagane' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Email jest wymagany' },
                            { type: 'email', message: 'Niepoprawny format email' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
